import { cloneDeep as _cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { FormDto } from '@/services/form/dto';
import { FormElement } from '@/types/forms/formElement';
import { FormField } from '@/types/forms/fields/formField';
import { FormList } from '@/types/forms/fields/formList';
import { TextField } from '@/types/forms/fields/textField';
import { UploadField } from '@/types/forms/fields/uploadField';
import { DateField } from '@/types/forms/fields/dateField';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import { modelService } from '@/services/form/modelService';

export type ParsedApplicationImport = {
    applications: FormDto[];
    error: string | null;
};

export type PreparedApplicationImport = {
    model: ApplicationForm;
    containsFiles: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getApplicationData(value: unknown): FormDto | null {
    if (!isRecord(value) || 'exportMeta' in value) {
        return null;
    }

    if ('data' in value) {
        return isRecord(value.data) ? (value.data as FormDto) : null;
    }

    return value as FormDto;
}

export function parseApplicationImport(text: string): ParsedApplicationImport {
    const trimmedText = text.trim();
    if (!trimmedText) {
        return { applications: [], error: null };
    }

    try {
        const parsed = JSON.parse(trimmedText) as unknown;
        const entries = Array.isArray(parsed) ? parsed : [parsed];
        const applications = entries
            .map(getApplicationData)
            .filter((entry): entry is FormDto => entry !== null);
        return applications.length
            ? { applications, error: null }
            : { applications: [], error: 'Keine Antragsdaten gefunden.' };
    } catch {
        const applications: FormDto[] = [];
        const lines = trimmedText.split(/\r?\n/);

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            if (!line.trim()) {
                continue;
            }

            try {
                const application = getApplicationData(JSON.parse(line));
                if (application) {
                    applications.push(application);
                }
            } catch {
                return {
                    applications: [],
                    error: `Zeile ${index + 1} ist kein gueltiges JSON.`,
                };
            }
        }

        return applications.length
            ? { applications, error: null }
            : { applications: [], error: 'Keine Antragsdaten gefunden.' };
    }
}

function randomLetter(letter: string): string {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    const replacement = alphabet.charAt(
        Math.floor(Math.random() * alphabet.length),
    );
    return /[A-ZÄÖÜ]/.test(letter) ? replacement.toUpperCase() : replacement;
}

export function anonymizeText(value: string): string {
    const preservedParts: string[] = [];
    const withPreservedStreetSuffixes = value.replace(
        /straße|strasse|str\./gi,
        (match) => {
            preservedParts.push(match);
            return `{${preservedParts.length - 1}}`;
        },
    );

    return withPreservedStreetSuffixes
        .replace(/[A-Za-zÄÖÜäöüß]/g, randomLetter)
        .replace(/\{(\d+)\}/g, (_, index: string) => preservedParts[+index]);
}

function randomDigit(): string {
    return String(Math.floor(Math.random() * 10));
}

function randomDifferentDigit(digit: string): string {
    const random = Math.floor(Math.random() * 9);
    return String(random >= Number(digit) ? random + 1 : random);
}

function anonymizeDigits(value: string): string {
    return value.replace(/\d/g, randomDifferentDigit);
}

function modulo97(value: string): number {
    return value
        .split('')
        .reduce((remainder, digit) => (remainder * 10 + Number(digit)) % 97, 0);
}

function anonymizeIban(): string {
    const bban = Array.from({ length: 18 }, randomDigit).join('');
    const checksum = String(98 - modulo97(`${bban}131400`)).padStart(2, '0');
    return `DE${checksum}${bban}`;
}

function anonymizeBirthday(): string {
    const day = String(1 + Math.floor(Math.random() * 28)).padStart(2, '0');
    const month = String(1 + Math.floor(Math.random() * 12)).padStart(2, '0');
    const year = 1940 + Math.floor(Math.random() * 61);
    return `${day}.${month}.${year}`;
}

function anonymizeGeoCoordinate(propertyName: string, value: string): string {
    if (propertyName === 'degrees') {
        return String(Math.floor(Math.random() * 90));
    }
    if (propertyName === 'minutes') {
        return String(1 + Math.floor(Math.random() * 59));
    }
    if (propertyName === 'seconds') {
        return `${1 + Math.floor(Math.random() * 59)},${randomDigit()}`;
    }
    return value;
}

function isSensitiveNumberField(propertyName: string): boolean {
    return [
        'taxnumber',
        'phonenumber',
        'meternumber',
        'zip',
        'housenumber',
    ].some((identifier) => propertyName.toLowerCase().includes(identifier));
}

function loadFormElement(element: FormElement, value: unknown): void {
    if (element instanceof FormField) {
        element.value = value as never;
        return;
    }

    if (element instanceof FormList) {
        if (!Array.isArray(value)) {
            return;
        }

        value.forEach((item, index) => {
            if (!element.items[index]) {
                element.createItem();
            }
            if (element.items[index]) {
                loadFormElement(element.items[index], item);
            }
        });
        return;
    }

    if (!isRecord(value)) {
        return;
    }

    Object.keys(value).forEach((key) => {
        const child = element[key as keyof FormElement];
        if (child instanceof FormElement) {
            loadFormElement(child, value[key]);
        }
    });
}

function loadApplicationDto(model: ApplicationForm, dto: FormDto): void {
    Object.keys(dto).forEach((key) => {
        const target = model[key as keyof ApplicationForm];
        if (target instanceof FormElement) {
            loadFormElement(target, dto[key]);
        } else if (Object.prototype.hasOwnProperty.call(model, key)) {
            ((model as unknown) as Record<string, unknown>)[key] = dto[key];
        }
    });
}

function anonymizeFormElement(
    element: FormElement,
    propertyName: string,
    email: string,
    anonymize: boolean,
): boolean {
    if (element instanceof UploadField) {
        const containsFile = element.value !== null;
        element.value = null;
        return containsFile;
    }

    if (element instanceof TextField) {
        if (propertyName.toLowerCase().includes('email')) {
            element.value = email;
        } else if (anonymize && propertyName.toLowerCase().includes('iban')) {
            element.value = anonymizeIban();
        } else if (
            anonymize &&
            ['degrees', 'minutes', 'seconds'].includes(propertyName)
        ) {
            element.value = anonymizeGeoCoordinate(
                propertyName,
                element.value || '',
            );
        } else if (anonymize && isSensitiveNumberField(propertyName)) {
            element.value = anonymizeDigits(anonymizeText(element.value || ''));
        } else if (anonymize && element.value) {
            element.value = anonymizeText(element.value);
        }
        return false;
    }

    if (element instanceof DateField) {
        if (anonymize && propertyName === 'birthday' && element.value) {
            element.value = anonymizeBirthday();
        }
        return false;
    }

    if (element instanceof FormList) {
        let containsFiles = false;
        element.items.forEach((item) => {
            containsFiles =
                anonymizeFormElement(item, propertyName, email, anonymize) ||
                containsFiles;
        });
        return containsFiles;
    }

    let containsFiles = false;
    Object.keys(element).forEach((key) => {
        const child = element[key as keyof FormElement];
        if (child instanceof FormElement) {
            containsFiles =
                anonymizeFormElement(child, key, email, anonymize) ||
                containsFiles;
        }
    });
    return containsFiles;
}

export function prepareApplicationImport(
    dto: FormDto,
    anonymize: boolean,
    email: string,
): PreparedApplicationImport {
    const model = modelService.getModelClone().application;
    loadApplicationDto(model, _cloneDeep(dto));
    let containsFiles = false;
    Object.keys(model).forEach((key) => {
        const element = model[key as keyof ApplicationForm];
        if (element instanceof FormElement) {
            containsFiles =
                anonymizeFormElement(element, key, email, anonymize) ||
                containsFiles;
        }
    });

    model.applicationId = uuidv4();
    model.lastModified = null;

    return { model, containsFiles };
}
